import type { NuxtResponse } from '~/shared/api/dto'
import type { AuthMeResponse } from '~/shared/schemas/auth.schema'
import type {
	CreateGrantAssignmentBody,
	CreateGrantBody,
	GrantApiRow,
	GrantAssignmentApiRow,
	GrantPreview,
	UpdateGrantAssignmentBody,
	UpdateGrantBody,
} from '~/shared/types/grants'
import type {
	CreateHeatingSystemBody,
	HeatingSystemApiRow,
	UpdateHeatingSystemBody,
} from '~/shared/types/heatingsystem'

export { type AuthUser } from '~/shared/schemas/auth.schema'

export const useUserService = () => {
	const requestHeaders = useRequestHeaders(['cookie'])

	const getUserBaseURL = () => ''

	const getUserFetchOptions = () => ({
		headers: requestHeaders,
		baseURL: getUserBaseURL(),
	})

	const userFetch = async <T = any>(url: string, options: any = {}) => {
		return $fetch<NuxtResponse<T>>(url, {
			...getUserFetchOptions(),
			...options,
		})
	}

	const userUseFetch = <T = any>(url: string, options: any = {}) => {
		return useFetch<NuxtResponse<T>>(url, {
			...getUserFetchOptions(),
			...options,
		})
	}

	const user = {
		me: () => {
			return userUseFetch<AuthMeResponse>('/api/user/me')
		},
		meAsync: async () => {
			return userFetch<AuthMeResponse>('/api/user/me')
		},
		logout: async () => {
			return userFetch<{ success: boolean }>('/api/user/logout', {
				method: 'POST',
			})
		},
		login: (email: string, password: string) => {
			return useFetch<NuxtResponse<AuthMeResponse>>('/api/user/login', {
				method: 'POST',
				body: {
					email,
					password,
				},
				...getUserFetchOptions(),
			})
		},
	}

	const cms = {
		createText: async (content: string, note?: string) => {
			return userFetch('/api/cms/texts', {
				method: 'POST',
				body: { content, note },
			})
		},
		updateText: async (id: string, data: { content?: string; note?: string }) => {
			return userFetch(`/api/cms/texts/${id}`, {
				method: 'PUT',
				body: data,
			})
		},
		deleteText: async (id: string) => {
			return userFetch(`/api/cms/texts/${id}`, {
				method: 'DELETE',
			})
		},
		createTip: async (label: string, text: string, photo?: string) => {
			return userFetch('/api/cms/tips', {
				method: 'POST',
				body: { label, text, photo },
			})
		},
		updateTip: async (id: string, data: { label?: string; text?: string; photo?: string }) => {
			return userFetch(`/api/cms/tips/${id}`, {
				method: 'PUT',
				body: data,
			})
		},
		deleteTip: async (id: string) => {
			return userFetch(`/api/cms/tips/${id}`, {
				method: 'DELETE',
			})
		},
	}

	const data = {
		heatingSystemsRaw: async (): Promise<NuxtResponse<HeatingSystemApiRow[]>> => {
			return userFetch<HeatingSystemApiRow[]>('/api/data/heating-systems?raw=true')
		},
		heatingSystemByEId: async (eId: string): Promise<NuxtResponse<HeatingSystemApiRow>> => {
			return userFetch<HeatingSystemApiRow>(`/api/data/heating-systems/eid/${encodeURIComponent(eId)}`)
		},
		createHeatingSystem: async (body: CreateHeatingSystemBody): Promise<NuxtResponse<HeatingSystemApiRow>> => {
			return userFetch<HeatingSystemApiRow>('/api/data/heating-systems', {
				method: 'POST',
				body,
			})
		},
		updateHeatingSystem: async (
			eId: string,
			body: UpdateHeatingSystemBody
		): Promise<NuxtResponse<HeatingSystemApiRow>> => {
			return userFetch<HeatingSystemApiRow>(`/api/data/heating-systems/eid/${encodeURIComponent(eId)}`, {
				method: 'PUT',
				body,
			})
		},
		deleteHeatingSystem: async (eId: string): Promise<NuxtResponse<HeatingSystemApiRow>> => {
			return userFetch<HeatingSystemApiRow>(`/api/data/heating-systems/eid/${encodeURIComponent(eId)}`, {
				method: 'DELETE',
			})
		},
		grantsPreview: async (): Promise<NuxtResponse<GrantPreview[]>> => {
			return userFetch<GrantPreview[]>('/api/data/grants')
		},
		grantsRaw: async (): Promise<NuxtResponse<GrantApiRow[]>> => {
			return userFetch<GrantApiRow[]>('/api/data/grants?raw=true')
		},
		grantByFId: async (fId: string): Promise<NuxtResponse<GrantApiRow>> => {
			return userFetch<GrantApiRow>(`/api/data/grants/fid/${encodeURIComponent(fId)}`)
		},
		createGrant: async (body: CreateGrantBody): Promise<NuxtResponse<GrantApiRow>> => {
			return userFetch<GrantApiRow>('/api/data/grants', {
				method: 'POST',
				body,
			})
		},
		updateGrant: async (
			fId: string,
			body: UpdateGrantBody
		): Promise<NuxtResponse<GrantApiRow>> => {
			return userFetch<GrantApiRow>(`/api/data/grants/fid/${encodeURIComponent(fId)}`, {
				method: 'PUT',
				body,
			})
		},
		deleteGrant: async (fId: string): Promise<NuxtResponse<GrantApiRow>> => {
			return userFetch<GrantApiRow>(`/api/data/grants/fid/${encodeURIComponent(fId)}`, {
				method: 'DELETE',
			})
		},
		grantAssignmentsByEId: async (eId: string): Promise<NuxtResponse<GrantAssignmentApiRow[]>> => {
			return userFetch<GrantAssignmentApiRow[]>(
				`/api/data/heating-systems/eid/${encodeURIComponent(eId)}/grant-assignments`
			)
		},
		createGrantAssignment: async (
			eId: string,
			body: Omit<CreateGrantAssignmentBody, 'E_ID'>
		): Promise<NuxtResponse<GrantAssignmentApiRow>> => {
			return userFetch<GrantAssignmentApiRow>(
				`/api/data/heating-systems/eid/${encodeURIComponent(eId)}/grant-assignments`,
				{ method: 'POST', body }
			)
		},
		updateGrantAssignment: async (
			id: string,
			body: UpdateGrantAssignmentBody
		): Promise<NuxtResponse<GrantAssignmentApiRow>> => {
			return userFetch<GrantAssignmentApiRow>(`/api/data/grant-assignments/${id}`, {
				method: 'PUT',
				body,
			})
		},
		deleteGrantAssignment: async (id: string): Promise<NuxtResponse<GrantAssignmentApiRow>> => {
			return userFetch<GrantAssignmentApiRow>(`/api/data/grant-assignments/${id}`, {
				method: 'DELETE',
			})
		},
	}

	return {
		getUserBaseURL,
		getUserFetchOptions,
		userFetch,
		userUseFetch,
		user,
		cms,
		data,
	}
}
